import Modal from "components/modal";
import ModalTemplate from "components/modal/modal-template";
import ToggleAudio from "components/toggleAudio";
import Typography from "components/typography";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { FormatAudioDuration } from "utils/dateConvert";
import { ITrack } from "types/musicData";

interface IProps {
  editTrack: () => void;
  track: ITrack;
  deleteTrack: () => void;
  number: number;
}
const TrackItem = ({ editTrack, track, deleteTrack, number }: IProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleLoadedMetadata = (event: any) => {
    const audioDuration = event.target.duration;
    setDuration(audioDuration);
  };

  const handleTimeUpdate = (event: any) => {
    const current = event.target.currentTime;
    setCurrentTime(current);
  };

  const TogglePlay = () => {
    if (audioRef.current && !isPlaying) {
      audioRef.current.play();
      setIsPlaying(true);
    }
    if (audioRef.current && isPlaying) {
      setIsPlaying(false);
      audioRef.current.pause();
    }
  };
  return (
    <Container>
      <audio
        controls
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        style={{ display: "none" }}
        ref={audioRef}
      >
        <source src={URL.createObjectURL(track.file)} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <TrackDetails>
        <ReorderIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M19 10.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 14.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 6.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 18.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </ReorderIcon>
        <Typography mr="1.2" size="s" color={Colors.white100}>
          {number}
        </Typography>
        <PlayTimeContainer>
          <ToggleAudio toggleAudio={() => TogglePlay()} />
          <Typography size="xs" mt="0.4" color={Colors.grey500}>
            {FormatAudioDuration(duration)}
          </Typography>
        </PlayTimeContainer>
        <TrackText>
          <Typography size="s" weight="medium" mb="0.4">
            {track.trackDetails?.title}
          </Typography>
          <Typography size="xs" weight="medium" color={Colors.grey500}>
            {track.trackArtists?.length ? track.trackArtists[0].name : ""}
            {/* •
            Vibe King */}
          </Typography>
        </TrackText>
      </TrackDetails>
      <TrackOptions>
        <TrackOption onClick={editTrack}>
          <Typography mr="0.4" color={Colors.yellow400} m_display="none">
            Edit
          </Typography>
          <OptionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 15"
              fill="none"
            >
              <path
                d="M8.37803 2.87906L8.91866 2.33838C9.81448 1.44255 11.2669 1.44255 12.1628 2.33838C13.0586 3.2342 13.0586 4.68663 12.1628 5.58246L11.6221 6.12313M8.37803 2.87906C8.37803 2.87906 8.44558 4.028 9.45935 5.04178C10.4731 6.05555 11.6221 6.12313 11.6221 6.12313M8.37803 2.87906L3.40728 7.84978C3.0706 8.18648 2.90226 8.35477 2.75749 8.54039C2.58671 8.75937 2.4403 8.99626 2.32083 9.24692C2.21956 9.45943 2.14428 9.6853 1.99371 10.137L1.35569 12.0511M11.6221 6.12313L6.65136 11.0939C6.31466 11.4305 6.14637 11.5989 5.96075 11.7436C5.74178 11.9144 5.50487 12.0609 5.25421 12.1803C5.04171 12.2816 4.81585 12.3568 4.36416 12.5074L2.45009 13.1455M1.35569 12.0511L1.19973 12.519C1.12564 12.7412 1.18349 12.9863 1.34917 13.152C1.51485 13.3177 1.75992 13.3755 1.98221 13.3014L2.45009 13.1455M1.35569 12.0511L2.45009 13.1455"
                stroke="#FEB73D"
                stroke-width="1.02083"
              />
            </svg>
          </OptionIcon>
        </TrackOption>
        <TrackOption onClick={() => setModalOpen(true)}>
          <Typography mr="0.4" color={Colors.danger} m_display="none">
            Delete
          </Typography>
          <OptionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 15"
              fill="none"
            >
              <path
                d="M11.9597 4H2.04297"
                stroke="#FF3333"
                stroke-width="0.875"
                stroke-linecap="round"
              />
              <path
                d="M10.9879 5.4585L10.7195 9.48297C10.6163 11.0317 10.5647 11.806 10.0601 12.2781C9.55549 12.7502 8.77943 12.7502 7.22729 12.7502H6.7762C5.22404 12.7502 4.44797 12.7502 3.94338 12.2781C3.4388 11.806 3.38717 11.0317 3.28392 9.48297L3.01562 5.4585"
                stroke="#FF3333"
                stroke-width="0.875"
                stroke-linecap="round"
              />
              <path
                d="M5.54297 6.9165L5.83464 9.83317"
                stroke="#FF3333"
                stroke-width="0.875"
                stroke-linecap="round"
              />
              <path
                d="M8.45964 6.9165L8.16797 9.83317"
                stroke="#FF3333"
                stroke-width="0.875"
                stroke-linecap="round"
              />
              <path
                d="M3.79297 4C3.82557 4 3.84186 4 3.85664 3.99963C4.33698 3.98745 4.76073 3.68203 4.92418 3.23019C4.92921 3.21629 4.93436 3.20083 4.94467 3.1699L5.0013 3C5.04965 2.85496 5.07382 2.78244 5.10588 2.72087C5.23381 2.47521 5.47048 2.30462 5.74399 2.26095C5.81254 2.25 5.889 2.25 6.04189 2.25H7.96071C8.1136 2.25 8.19008 2.25 8.25862 2.26095C8.53214 2.30462 8.7688 2.47521 8.89673 2.72087C8.92881 2.78244 8.95296 2.85496 9.00132 3L9.05796 3.1699C9.06823 3.20079 9.07342 3.2163 9.07844 3.23019C9.24189 3.68203 9.66562 3.98745 10.146 3.99963C10.1608 4 10.177 4 10.2096 4"
                stroke="#FF3333"
                stroke-width="0.875"
              />
            </svg>
          </OptionIcon>
        </TrackOption>
      </TrackOptions>
      <CheckMark>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 24 25"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5C17.5228 2.5 22 6.97715 22 12.5ZM16.0303 9.46967C16.3232 9.76256 16.3232 10.2374 16.0303 10.5303L11.0303 15.5303C10.7374 15.8232 10.2626 15.8232 9.96967 15.5303L7.96967 13.5303C7.67678 13.2374 7.67678 12.7626 7.96967 12.4697C8.26256 12.1768 8.73744 12.1768 9.03033 12.4697L10.5 13.9393L12.7348 11.7045L14.9697 9.46967C15.2626 9.17678 15.7374 9.17678 16.0303 9.46967Z"
            fill="#5AB400"
          />
        </svg>
      </CheckMark>
      {modalOpen && (
        <Modal isActive={modalOpen} closeModal={() => setModalOpen(false)}>
          <ModalTemplate
            close={() => {
              setModalOpen(false);
            }}
            confirm={() => {
              deleteTrack();
            }}
            confirmText="Confirm"
            header="Delete Track?"
            cancelText="Cancel"
            twoButtons
          >
            <Typography align="center">
              Are you sure you want to delete the track {track.file.name}?
            </Typography>
          </ModalTemplate>
        </Modal>
      )}
    </Container>
  );
};

export default TrackItem;

const Container = styled.div`
  background-color: ${Colors.grey600};
  padding: 2rem 1.2rem;
  border-radius: 8px;
  margin-bottom: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const TrackDetails = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
`;

const ReorderIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  margin-right: 1.2rem;
`;
const PlayTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 2.4rem;
`;

const TrackText = styled.div``;
const TrackOptions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 18%;
  @media only screen and (max-width: 769px) {
    width: 15%;
  }
`;
const TrackOption = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const OptionIcon = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  @media only screen and (max-width: 769px) {
    width: 2rem;
    height: 2rem;
  }
`;

const CheckMark = styled.div`
  width: 2.4rem;
  height: 2.4rem;
`;
